/**
 * BookingDataContext - Global Booking Data Cache
 * 
 * Provides a single source of truth for booking data from local MongoDB.
 * Implements the "2-Second Rule" by:
 * - Fetching all bookings once on initial load
 * - Caching processed data in React context
 * - Enabling instant tab switching between Calendar and Analytics
 * 
 * IMPORTANT: Bookings are filtered by property_id to ensure hotel owners
 * only see their own property's bookings (data security).
 * 
 * REAL-TIME SYNC: Uses WebSocket for instant updates from Admin Panel.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { bookingsAPI, notificationAPI } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { toast } from '../hooks/use-toast';
import { cacheBookings, getCachedBookings } from '../services/offlineStorage';
import { sendBrowserNotification } from '../services/browserNotification';

const BookingDataContext = createContext(null);

export const useBookingData = () => {
  const context = useContext(BookingDataContext);
  if (!context) {
    throw new Error('useBookingData must be used within BookingDataProvider');
  }
  return context;
};

export const BookingDataProvider = ({ children }) => {
  const { selectedProperty, isAuthenticated, user } = useAuth();
  
  // Core state
  const [allBookings, setAllBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState('local');
  const [lastFetched, setLastFetched] = useState(null);
  const [error, setError] = useState(null);
  const [realtimeUpdates, setRealtimeUpdates] = useState([]);
  
  // Track if initial load has happened
  const initialLoadDone = useRef(false);
  
  // Get the current property ID for filtering
  const getPropertyId = useCallback(() => {
    // Priority: selectedProperty > user's first property > null
    if (selectedProperty?.id) return selectedProperty.id;
    if (selectedProperty?.property_id) return selectedProperty.property_id;
    if (user?.properties?.[0]?.id) return user.properties[0].id;
    if (user?.property_id) return user.property_id;
    return null;
  }, [selectedProperty, user]);
  
  // Get GRB ID for booking matching (hidden, never shown in UI)
  const getGrbId = useCallback(() => {
    return selectedProperty?.grb_id || user?.properties?.[0]?.grb_id || null;
  }, [selectedProperty, user]);
  
  // Get property name for booking matching (legacy bookings use name as property_id)
  const getPropertyName = useCallback(() => {
    return selectedProperty?.property_name || selectedProperty?.name || 
           user?.properties?.[0]?.property_name || null;
  }, [selectedProperty, user]);
  
  // Handle real-time booking updates from WebSocket
  const handleBookingUpdate = useCallback((payload) => {
    console.log('[BookingData] Real-time booking update:', payload.type);
    
    const { type, data } = payload;
    const propertyId = getPropertyId();
    
    // Only process if matches our property (or no property filter)
    if (propertyId && data?.property_id && data.property_id !== propertyId) {
      return;
    }
    
    setRealtimeUpdates(prev => [{ ...payload, receivedAt: new Date() }, ...prev].slice(0, 20));
    
    // Push in-app notification
    const guestName = data?.guest_name || 'Guest';
    const otaSource = data?.ota_source || '';
    
    switch (type) {
      case 'create':
        setAllBookings(prev => {
          const exists = prev.some(b => b.id === data.id || b.booking_id === data.booking_id);
          if (exists) return prev;
          return [data, ...prev];
        });
        setNotifications(prev => [{
          id: `ws-new-${data?.booking_id || Date.now()}`,
          type: 'new_booking',
          title: 'New Booking Received',
          message: `${guestName}${otaSource ? ` via ${otaSource}` : ''}`,
          timestamp: new Date(),
          read: false,
          // iter142 — Attach booking_id so the bell click can open the booking.
          booking_id: data?.booking_id || data?.id || '',
          data: { booking_id: data?.booking_id || data?.id || '' },
        }, ...prev].slice(0, 50));
        {
          const checkInDate = data?.check_in_date || data?.check_in || '';
          const checkInFormatted = checkInDate ? checkInDate.split('T')[0] : '';
          toast({
            title: `New booking received from ${otaSource || 'Admin Panel'}!`,
            description: `Guest: ${guestName}${checkInFormatted ? ` | Check-in: ${checkInFormatted}` : ''}`,
            variant: 'sync',
            duration: 5000,
          });
          sendBrowserNotification(
            'New Booking Received',
            `${guestName}${otaSource ? ` via ${otaSource}` : ''}${checkInFormatted ? ` | Check-in: ${checkInFormatted}` : ''}`,
            `booking-${data?.booking_id}`
          );
        }
        break;
        
      case 'update':
        setAllBookings(prev => 
          prev.map(booking => 
            (booking.id === data.id || booking.booking_id === data.booking_id)
              ? { ...booking, ...data } : booking
          )
        );
        break;

      case 'cancel':
        setAllBookings(prev => 
          prev.map(booking => 
            (booking.id === data.id || booking.booking_id === data.booking_id)
              ? { ...booking, ...data, booking_status: 'cancelled', status: 'cancelled', is_cancelled: true } : booking
          )
        );
        setNotifications(prev => [{
          id: `ws-cancel-${data?.booking_id || Date.now()}`,
          type: 'booking_cancelled',
          title: 'Booking Cancelled',
          message: `${guestName}${otaSource ? ` via ${otaSource}` : ''}`,
          timestamp: new Date(),
          read: false,
          // iter142 — Attach booking_id so the bell click can open the booking.
          booking_id: data?.booking_id || data?.id || '',
          data: { booking_id: data?.booking_id || data?.id || '' },
        }, ...prev].slice(0, 50));
        {
          toast({
            title: `Booking Cancelled`,
            description: `Guest: ${guestName}${otaSource ? ` | ${otaSource}` : ''}`,
            variant: 'destructive',
            duration: 5000,
          });
          sendBrowserNotification(
            'Booking Cancelled',
            `${guestName}${otaSource ? ` | ${otaSource}` : ''}`,
            `cancel-${data?.booking_id}`
          );
        }
        break;
        
      case 'delete':
        setAllBookings(prev => 
          prev.filter(booking => 
            booking.id !== data.id && booking.booking_id !== data.booking_id
          )
        );
        setNotifications(prev => [{
          id: Date.now(),
          type: 'booking_deleted',
          title: 'Booking Deleted',
          message: `${guestName} booking removed`,
          timestamp: new Date(),
          read: false,
        }, ...prev].slice(0, 50));
        {
          toast({
            title: `Booking deleted`,
            description: `Guest: ${guestName} | ID: ${data?.booking_id || ''}`,
            variant: 'destructive',
            duration: 4000,
          });
        }
        break;
        
      default:
        break;
    }
  }, [getPropertyId]);
  
  // Handle real-time lead updates from WebSocket
  const handleLeadUpdate = useCallback((payload) => {
    console.log('[BookingData] Real-time lead update:', payload.type);
    const { type, data } = payload;
    
    if (type === 'created') {
      setNotifications(prev => [{
        id: Date.now(),
        type: 'new_lead',
        title: 'New Lead Added',
        message: `${data?.owner_name || 'New lead'}${data?.property_name ? ` — ${data.property_name}` : ''}`,
        timestamp: new Date(),
        read: false,
      }, ...prev].slice(0, 50));
    }
  }, []);
  
  // Mark notification as read
  const markNotificationRead = useCallback((notifId) => {
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    // iter137 — persist read state on server so it stays cleared across
    // refreshes and sessions. Fire-and-forget; UI updates instantly.
    if (notifId) {
      notificationAPI.markOneRead(notifId).catch((e) => {
        console.warn('[Notifications] markOneRead failed', e);
      });
    }
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(async () => {
    // iter137 — Persist "all-read" state on the server so:
    //   • Badge stays 0 after refresh / re-login
    //   • Empty state is authoritative, not a stale UI illusion
    //   • Any device seeing the same account gets the update on next fetch
    setNotifications([]);
    try {
      await notificationAPI.markAllRead();
    } catch (e) {
      console.warn('[Notifications] markAllRead failed', e);
    }
  }, []);
  
  // Setup WebSocket connection with real-time updates
  const { isConnected: wsConnected } = useWebSocket({
    phone: user?.phone,
    propertyId: getPropertyId(),
    onBookingUpdate: handleBookingUpdate,
    onLeadUpdate: handleLeadUpdate,
    autoConnect: isAuthenticated,
  });
  
  // Fetch all bookings from local MongoDB - FILTERED BY PROPERTY
  // `silent` = true keeps loading=false so the calendar never visually re-renders the spinner.
  const fetchAllBookings = useCallback(async (forceRefresh = false, silent = false) => {
    if (!isAuthenticated) {
      console.log('[BookingData] Not authenticated, skipping fetch');
      setLoading(false);
      return [];
    }
    
    const propertyId = getPropertyId();
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Fetch bookings from local MongoDB — filter by grb_id (priority), then property_id + name
      const propertyName = getPropertyName();
      const grbId = getGrbId();
      const response = await bookingsAPI.getAll(propertyId, null, null, propertyName, grbId);
      
      console.log('[BookingData] API Response:', {
        STATUS: response?.STATUS,
        MESSAGE: response?.MESSAGE,
        resultLength: response?.RESULT?.length
      });
      
      // Handle response format
      let bookings = [];
      if (response?.STATUS === 1 && response?.RESULT) {
        bookings = Array.isArray(response.RESULT) ? response.RESULT : [];
      } else if (response?.data) {
        bookings = Array.isArray(response.data) ? response.data : [];
      } else if (response?.bookings) {
        bookings = Array.isArray(response.bookings) ? response.bookings : [];
      } else if (Array.isArray(response)) {
        bookings = response;
      }
      
      // iter284 — Additional client-side filter as safety measure.
      // Previously this also matched on `property_name === propName`
      // and `property_id === propName`, which collapsed records across
      // different properties that shared a hotel_name string (and in
      // one case accepted a GRB id as a property-id match). Keep the
      // filter strict: UUID property_id OR grb_property_id only.
      if (propertyId && bookings.length > 0) {
        const beforeFilter = bookings.length;
        bookings = bookings.filter(b =>
          b.property_id === propertyId ||
          b.propertyId === propertyId ||
          b.grb_property_id === propertyId ||
          !b.property_id // Include bookings without property_id
        );
        console.log(`[BookingData] Client-side property filter: ${beforeFilter} -> ${bookings.length} bookings`);
      }
      
      console.log(`[BookingData] Parsed ${bookings.length} bookings for property ${propertyId || 'ALL'}`);
      
      // Update state + cache for offline
      setAllBookings(bookings);
      setDataSource('local');
      setLastFetched(Date.now());
      cacheBookings(bookings);
      
      return bookings;
    } catch (err) {
      const errorMessage = err?._friendlyMessage || err?.message || 'Failed to fetch bookings';
      console.error('[BookingData] Error fetching bookings:', errorMessage);
      
      // Offline fallback: load from cache
      const cached = getCachedBookings();
      if (cached.length > 0) {
        console.log(`[BookingData] Offline fallback: loaded ${cached.length} cached bookings`);
        setAllBookings(cached);
        setDataSource('cache');
        return cached;
      }
      
      setError(errorMessage);
      return allBookings;
    } finally {
      if (!silent) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, isAuthenticated, getPropertyId]);
  
  // Fetch notifications — iter137: hydrate the bell from the server so that
  // notifications marked as read on any device stay cleared after a reload.
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return [];
    try {
      const res = await notificationAPI.list({ limit: 50, unread_only: true });
      const rows = res?.data?.RESULT || res?.RESULT || [];
      const mapped = (Array.isArray(rows) ? rows : []).map((n, idx) => ({
        id: n.id || n._id || `srv-${idx}-${n.created_at || ''}`,
        type: n.type || 'general',
        title: n.title || 'Notification',
        message: n.body || n.message || '',
        timestamp: n.created_at ? new Date(n.created_at) : new Date(),
        read: !!n.is_read,
        data: n.data || {},
        // iter142 — Surface booking_id at the top level so bell click handlers
        // (CalendarScreen) can navigate to the related booking without having
        // to dig into `data`.
        booking_id: (n.data && (n.data.booking_id || n.data.bookingId)) || '',
      }));
      setNotifications(mapped);
      return mapped;
    } catch (e) {
      console.warn('[Notifications] fetchNotifications failed', e);
      return [];
    }
  }, [isAuthenticated]);
  
  // Refresh all data
  const refreshData = useCallback(async () => {
    const [bookings] = await Promise.all([
      fetchAllBookings(true),
      fetchNotifications()
    ]);
    return { bookings, notifications: [] };
  }, [fetchAllBookings, fetchNotifications]);
  
  // Initial data load on mount and when auth changes
  useEffect(() => {
    if (isAuthenticated && !initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchAllBookings(true);
      fetchNotifications();
    }
  }, [isAuthenticated, fetchAllBookings, fetchNotifications]);
  
  // Reset when property changes
  useEffect(() => {
    if (selectedProperty?.id && initialLoadDone.current) {
      console.log('[BookingData] Property changed, refreshing data...');
      fetchAllBookings(true);
    }
  }, [selectedProperty?.id, fetchAllBookings]);
  
  // Real-time sync:
  //   1. Refetch on app visibility / focus (covers PWA resume + tab switch).
  //   2. Silent 60s polling — never flips `loading=true`, so calendar UI never reloads.
  //   3. Online event — recover after a flaky network blip.
  useEffect(() => {
    if (!isAuthenticated) return;

    const refresh = () => fetchAllBookings(true, /* silent */ true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);

    // Silent background poll — every 60s while page visible.
    const pollId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    }, 60_000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      clearInterval(pollId);
    };
  }, [isAuthenticated, fetchAllBookings]);
  
  // Helper: Get calendar counts for a date range
  const getCalendarCounts = useCallback((startDate, endDate) => {
    const counts = {
      checkin: {},
      checkout: {},
      booking: {}
    };
    
    allBookings.forEach(booking => {
      // Check-in counts
      const checkinDate = booking.check_in_date || booking.check_in || '';
      if (checkinDate) {
        const dateKey = checkinDate.split('T')[0];
        counts.checkin[dateKey] = (counts.checkin[dateKey] || 0) + 1;
      }
      
      // Check-out counts
      const checkoutDate = booking.check_out_date || booking.check_out || '';
      if (checkoutDate) {
        const dateKey = checkoutDate.split('T')[0];
        counts.checkout[dateKey] = (counts.checkout[dateKey] || 0) + 1;
      }
      
      // Booking date counts
      const bookingDate = booking.booked_on_date || booking.booking_date || booking.created_at || '';
      if (bookingDate) {
        const dateKey = bookingDate.split('T')[0];
        counts.booking[dateKey] = (counts.booking[dateKey] || 0) + 1;
      }
    });
    
    return counts;
  }, [allBookings]);
  
  // Helper: Filter bookings for a specific month
  const filterBookingsForMonth = useCallback((bookings, monthStart, monthEnd, filterType = 'checkin') => {
    const startStr = monthStart.toISOString().split('T')[0];
    const endStr = monthEnd.toISOString().split('T')[0];
    
    return bookings.filter(booking => {
      let dateToCheck = '';
      
      if (filterType === 'checkin') {
        dateToCheck = booking.check_in_date || booking.check_in || '';
      } else if (filterType === 'checkout') {
        dateToCheck = booking.check_out_date || booking.check_out || '';
      } else {
        dateToCheck = booking.booked_on_date || booking.booking_date || booking.created_at || '';
      }
      
      const dateStr = dateToCheck.split('T')[0];
      return dateStr >= startStr && dateStr <= endStr;
    });
  }, []);
  
  // Helper: Get bookings for a specific date
  const getBookingsForDate = useCallback((date, filterType = 'checkin') => {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    
    return allBookings.filter(booking => {
      let bookingDate = '';
      
      if (filterType === 'checkin') {
        bookingDate = booking.check_in_date || booking.check_in || '';
      } else if (filterType === 'checkout') {
        bookingDate = booking.check_out_date || booking.check_out || '';
      } else {
        bookingDate = booking.booked_on_date || booking.booking_date || booking.created_at || '';
      }
      
      return bookingDate.startsWith(dateStr);
    });
  }, [allBookings]);
  
  // Calculate analytics from cached data
  const getAnalytics = useCallback((startDate = null, endDate = null) => {
    let filteredBookings = allBookings;
    
    if (startDate && endDate) {
      filteredBookings = allBookings.filter(b => {
        const bookingDate = b.check_in_date || (b.check_in || '').split('T')[0] || '';
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    }
    
    const total_bookings = filteredBookings.length;
    
    // Separate cancelled bookings
    const cancelledBookings = filteredBookings.filter(b => {
      const st = (b.booking_status || b.status || '').toLowerCase();
      return st === 'cancelled' || st === 'rejected' || b.is_cancelled;
    });
    const activeBookings = filteredBookings.filter(b => {
      const st = (b.booking_status || b.status || '').toLowerCase();
      return st !== 'cancelled' && st !== 'rejected' && !b.is_cancelled;
    });
    const cancelled_count = cancelledBookings.length;
    
    // Revenue ONLY from active (non-cancelled) bookings
    const total_revenue = activeBookings.reduce((sum, b) => sum + (parseFloat(b.amount) || parseFloat(b.total_amount) || parseFloat(b.total_price) || 0), 0);
    const average_booking_value = activeBookings.length > 0 ? total_revenue / activeBookings.length : 0;
    
    // OTA vs Walk-in counts (all bookings including cancelled)
    const ota_bookings = filteredBookings.filter(b => {
      const src = (b.ota_source || '').toLowerCase();
      return src !== 'walk-in' && src !== 'direct' && src !== '';
    }).length;
    const offline_bookings = total_bookings - ota_bookings;
    
    // OTA source breakdown (count-based — all bookings)
    const ota_breakdown = {};
    filteredBookings.forEach(b => {
      const source = b.ota_source || 'Walk-in';
      ota_breakdown[source] = (ota_breakdown[source] || 0) + 1;
    });
    
    // OTA revenue breakdown — ONLY active bookings
    const ota_revenue_breakdown = {};
    activeBookings.forEach(b => {
      const source = b.ota_source || 'Walk-in';
      const amt = parseFloat(b.amount) || parseFloat(b.total_amount) || parseFloat(b.total_price) || 0;
      ota_revenue_breakdown[source] = (ota_revenue_breakdown[source] || 0) + amt;
    });
    
    // Daily bookings (check-in based)
    const daily_bookings = {};
    const daily_revenue = {};
    filteredBookings.forEach(b => {
      const date = b.check_in_date || (b.check_in || '').split('T')[0] || '';
      if (date) {
        daily_bookings[date] = (daily_bookings[date] || 0) + 1;
        // Revenue only from active
        const st = (b.booking_status || b.status || '').toLowerCase();
        const isActive = st !== 'cancelled' && st !== 'rejected' && !b.is_cancelled;
        if (isActive) {
          daily_revenue[date] = (daily_revenue[date] || 0) + (parseFloat(b.amount) || parseFloat(b.total_amount) || 0);
        }
      }
    });
    
    // Group by property
    const propertyBreakdown = {};
    activeBookings.forEach(b => {
      const property = b.property_name || b.master_hotel_name || 'Unknown';
      if (!propertyBreakdown[property]) {
        propertyBreakdown[property] = { revenue: 0, bookings: 0 };
      }
      propertyBreakdown[property].revenue += parseFloat(b.amount) || parseFloat(b.total_amount) || 0;
      propertyBreakdown[property].bookings += 1;
    });
    
    return {
      total_bookings,
      total_revenue,
      average_booking_value,
      ota_bookings,
      offline_bookings,
      cancelled_count,
      ota_breakdown,
      ota_revenue_breakdown,
      daily_bookings,
      daily_revenue,
      propertyBreakdown,
    };
  }, [allBookings]);
  
  const value = {
    // Data
    allBookings,
    notifications,
    loading,
    dataSource,
    lastFetched,
    error,
    
    // Real-time sync status
    wsConnected,
    realtimeUpdates,
    
    // Actions
    fetchAllBookings,
    fetchNotifications,
    refreshData,
    markNotificationRead,
    clearNotifications,
    
    // Helpers
    getCalendarCounts,
    filterBookingsForMonth,
    getBookingsForDate,
    getAnalytics,
    
    // Computed
    totalBookings: allBookings.length,
    unreadNotifications: notifications.filter(n => !n.read).length,
  };

  return (
    <BookingDataContext.Provider value={value}>
      {children}
    </BookingDataContext.Provider>
  );
};

export default BookingDataContext;
